import React from "react";
import { RenderMediaContent } from "src/components/RenderMediaContent";
import { WarningAlert } from "src/components/WarningAlert";

import dhServerIcon from '../../assets/setup/server/dhServerIcon.png';
import dhObsDockSetup from '../../assets/setup/server/dhObsDockSetup.png';
import dhObsDockSetup2 from '../../assets/setup/server/dhObsDockSetup2.png';
import dhTrayIcon from '../../assets/setup/server/dhTrayIcon.png';
import dhTrayIcon2 from '../../assets/setup/server/dhTrayIcon2.png';
import dhAccount from '../../assets/setup/server/dhAccount.png';
import dhServerBrowser from '../../assets/setup/server/dhServerBrowser.png';
import dhServerObs from '../../assets/setup/server/dhServerObs.png';


export const DeckHopperServer: React.FC = () => {
    return (
        <div className="flex flex-col gap-6 px-4">
            <p className="text-sm">
                Available in Deck Hopper 1.6 and up.
            </p>

            <section className="flex flex-col gap-3">
                <p>
                    Deck Hopper can run directly in your browser or inside an OBS Custom
                    Browser Dock.
                </p>

                <p>
                    Once the setup below is done, you can simply run Deck Hopper Server every time you want to use Deck Hopper in a browser or OBS.
                </p>

                <WarningAlert message="Currently only available on Windows. macOS and Linux support coming soon." />
            </section>



            <section className="flex flex-col gap-3">
                <h3 className="text-lg font-semibold">Setup Steps (Windows 11)</h3>
                <RenderMediaContent src="https://www.youtube.com/watch?v=hr1BoEp6uvo" />
                <ol className="list-decimal space-y-2 pl-6">
                    <li>
                        Install <b>Deck Hopper</b>, then open it from the <b>Start</b> menu.
                    </li>
                    <li>
                        Log in to your <b>Deck Hopper Pro</b> account. Confirm your license
                        displays <b>"Deck Hopper Pro"</b>. You only need to do this once.
                        <div className="mt-2"><RenderMediaContent src={dhAccount} /></div>
                    </li>
                    <li>
                        Close Deck Hopper completely.
                    </li>
                    <li>
                        Open <b>Deck Hopper Server</b> from the <b>Start</b> menu. A new
                        tray icon will appear. (There is no main app window for the server.)

                        {
                            /*
                        If you're on macOS or Linux, launch Deck Hopper with the following flag: `--server`. 
                        Example: `/Applications/Deck\ Hopper.app/Contents/MacOS/Deck\ Hopper --server`
                        */
                        }

                        <div className="mt-2"><RenderMediaContent src={dhServerIcon} /></div>
                    </li>
                    <li>
                        Right–click the tray icon → <b>Open in default browser</b>. You can
                        now use Deck Hopper in your browser. (You may need to log in again.)
                        <div className="mt-2"><RenderMediaContent src={dhTrayIcon2} /></div>
                        <div className="mt-2"><RenderMediaContent src={dhServerBrowser} /></div>
                    </li>
                    <li>
                        Right–click the tray icon → <b>Copy network address</b>.
                        <div className="mt-2"><RenderMediaContent src={dhTrayIcon} /></div>
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                            <li>
                                Use this address on another device on the same network to open
                                Deck Hopper in a browser.
                            </li>
                            <li>
                                To add Deck Hopper as an OBS Dock: <b>OBS</b> → <b>Docks</b> →{" "}
                                <b>Custom Browser Docks…</b> → paste the network address in the
                                URL field → <b>Apply</b>. (You may need to log in again.)
                                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <RenderMediaContent src={dhObsDockSetup2} />
                                    <RenderMediaContent src={dhObsDockSetup} />
                                    <RenderMediaContent src={dhServerObs} />
                                </div>
                            </li>
                        </ul>
                    </li>
                    <li>
                        Next time you want to use Deck Hopper in a browser or OBS, you can simply run Deck Hopper Server from the <b>Start</b> menu, and it will be ready to use.
                    </li>
                </ol>

                <section className="flex flex-col gap-4">
                    <h3 className="text-lg font-semibold">Auto-launch Deck Hopper Server at startup</h3>
                    <RenderMediaContent src="https://www.youtube.com/watch?v=vCeJXSzwABc" />
                    <p>Start Deck Hopper Server automatically whenever Windows starts.</p>
                    <ol className="list-decimal space-y-2 pl-6">
                        <li>
                            Open <b>Start</b>, search for <b>Deck Hopper Server</b>, right-click it, and choose <b>Open file location</b>.
                        </li>
                        <li>
                            Copy the <b>Deck Hopper Server</b> shortcut. You can also create a new shortcut to your <b>Deck Hopper.exe</b> and add these flags:&nbsp;
                            <code>--server --no-window</code>.
                        </li>
                        <li>
                            Press <b>Win</b>+<b>R</b>, type <code>shell:startup</code>, and press <b>Enter</b>.
                        </li>
                        <li>
                            Paste the shortcut into the <b>Startup</b> folder.
                        </li>
                    </ol>
                    <p className="text-sm text-gray-300">
                        That’s it — Deck Hopper Server will start automatically the next time Windows boots.
                    </p>
                     
                </section>


                <div className="rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm flex items-start gap-3 mt-8">
                    <span className="mt-1 text-gray-400">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                            <circle cx="10" cy="10" r="10" fill="#1f2937" />
                            <text x="10" y="15" textAnchor="middle" fontSize="12" fill="#d1d5db" fontFamily="Arial, Helvetica, sans-serif">i</text>
                        </svg>
                    </span>
                    <p className="m-0 text-gray-200">
                        If you right–click the tray icon → <b>Exit</b>, Deck Hopper Server stops. It won’t be accessible in the browser or OBS until you start it again.
                    </p>
                </div>

                <div className="rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm flex items-start gap-3">
                    <span className="mt-1 text-gray-400">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#1f2937" /><path d="M10 6v4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" /><circle cx="10" cy="13.5" r="1" fill="#d1d5db" /></svg>
                    </span>
                    <p className="m-0 text-gray-200">
                        You can run regular <b>Deck Hopper</b> and <b>Deck Hopper Server</b> at the same time.
                    </p>
                </div>

                <div className="rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm flex items-start gap-3">
                    <span className="mt-1 text-gray-400">
                        <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><circle cx="10" cy="10" r="10" fill="#1f2937" /><path d="M10 6v4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" /><circle cx="10" cy="13.5" r="1" fill="#d1d5db" /></svg>
                    </span>
                    <p className="m-0 text-gray-200">
                        <b>Port:</b> Deck Hopper Server runs on <b>3443</b> (not configurable).
                    </p>
                </div>
            </section>
        </div>
    );
};

export default DeckHopperServer;
